import Info from '@clearkit/icons/glyphs/Info';
import { CKButton } from '@clearkit/react';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import Menu from '@material-ui/core/Menu';
import { Link } from 'react-router-dom';

import ContentBox from '~/components/ContentBox';
import EllipsesIcon from '~/components/icons/EllipsesIcon';
import MenuItem from '~/components/RedesignedMenuItem';
import RedesignedSelect from '~/components/RedesignedSelect';
import RedesignedTextField from '~/components/RedesignedTextField';
import SearchBuilder from '~/components/SearchBuilder';
import validateSearch from '~/components/SearchBuilder/utils/validateSearch.js';
import GoIcon from '~/images/Icons/Glyphs/Go.svg';
import CompaniesTile from '~/images/Tiles/Segment/Company.svg';
import PeopleTile from '~/images/Tiles/Segment/Person.svg';
import { audienceSubtypes } from '~/lib/audiences';
import paths from '~/lib/paths';
import { segmentTypes } from '~/lib/segments';
import FolderIcon from '~/pages/audiences/components/SegmentForm/SegmentPicker/Folder.svg';

import CloneIcon from './Clone.svg';
import SegmentIcon from './SegmentIcon.svg';
import styles from './styles';

function SegmentBuilder({
  segment,
  onClone,
  onCancel,
  name,
  classes,
  searchAttributes,
  search,
  collections,
  onChange,
  collectionId,
  disabled,
  subtype,
  title,
  showPreview = true,
  actions,
}) {
  const [validSearch, setValidSearch] = useState(
    validateSearch(search).length === 0,
  );

  const setName = (name) => {
    const valid = validSearch && !!name && !!collectionId;
    onChange({ collectionId, search, name }, valid);
  };

  const setSearch = (search, searchValid) => {
    setValidSearch(searchValid);
    const valid = searchValid && !!name && !!collectionId;
    onChange({ collectionId, search, name }, valid);
  };

  const setCollection = (collectionId) => {
    const valid = validSearch && !!name && !!collectionId;
    onChange({ collectionId, search, name }, valid);
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const moreIcon = useRef(undefined);

  let footer;
  switch (subtype) {
    case audienceSubtypes.visitors:
      footer =
        'All people arriving on your site — who work at companies matching this criteria — will be included in your advertising audience.';
      break;
    default:
      break;
  }

  const titleElement = () => {
    if (title) return title;

    if (segment.id)
      return (
        <>
          <ContentBox.Icon>
            {segment.type === segmentTypes.company ? (
              <CompaniesTile width={40} />
            ) : (
              <PeopleTile width={40} />
            )}
          </ContentBox.Icon>
          <div className={classes.contentBit}>
            <span>{segment.name}</span>
            <span>
              <FolderIcon className={classes.folderIcon} />
              {segment.collection?.name}
            </span>
          </div>
        </>
      );

    return (
      <>
        <ContentBox.Icon>
          {segment.type === segmentTypes.company ? (
            <CompaniesTile width={40} />
          ) : (
            <PeopleTile width={40} />
          )}
        </ContentBox.Icon>
        <RedesignedTextField
          className={classes.formField}
          placeholder="Name your segment"
          onChange={(event) => setName(event.target.value)}
          value={name}
          disabled={disabled}
        />
        {collections && (
          <RedesignedSelect
            disabled={disabled}
            className={classes.formField}
            onChange={(event) => setCollection(event.target.value)}
            placeholder="Choose a collection"
            variant="standard"
            value={collectionId}>
            {collections.map((collection) => (
              <MenuItem value={collection.id} key={collection.id}>
                {collection.name}
              </MenuItem>
            ))}
          </RedesignedSelect>
        )}
      </>
    );
  };

  return (
    <>
      <ContentBox.Section className={classes.header} variant="horizontal">
        {titleElement()}

        <div className={classes.actions}>
          {actions}
          <div
            ref={moreIcon}
            className={classnames(classes.formField, classes.moreButton)}>
            {!disabled && (
              <CKButton
                datatest="segment-picker-actions-menu"
                onClick={(event) => {
                  event.preventDefault();
                  setMenuOpen(!menuOpen);
                }}>
                <EllipsesIcon />

                <Menu
                  anchorEl={moreIcon.current}
                  open={menuOpen}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  getContentAnchorEl={null}
                  onClose={() => setMenuOpen(false)}>
                  {segment.id && (
                    <MenuItem
                      onClick={() => {
                        onClone();
                      }}>
                      <ListItemIcon>
                        <CloneIcon />
                      </ListItemIcon>
                      Clone and edit segment
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={() => {
                      onCancel();
                    }}>
                    <ListItemIcon>
                      <SegmentIcon />
                    </ListItemIcon>
                    Choose another segment
                  </MenuItem>
                </Menu>
              </CKButton>
            )}
          </div>
        </div>
      </ContentBox.Section>
      {showPreview && (
        <>
          <ContentBox.Section variant="grey">
            {segment.id && (
              <div className={classes.previewNotice}>
                <p className={classes.previewNoticeTitle}>Segment preview </p>
                <p>
                  To make changes to this existing segment, use the{' '}
                  <Link
                    to={paths.segmentEdit(segment.type, segment.id)}
                    target="_blank"
                    className="inline-flex">
                    <span>segment builder</span>
                    <GoIcon className="ml-1" />
                  </Link>
                </p>
              </div>
            )}
            <SearchBuilder
              disabled={!!segment.id}
              attributes={searchAttributes}
              type={segment.legacyType}
              search={search}
              onChange={setSearch}
            />
          </ContentBox.Section>
          {footer && (
            <ContentBox.Section className={classes.footer} variant="horizontal">
              <Info />
              {footer}
            </ContentBox.Section>
          )}
        </>
      )}
    </>
  );
}

SegmentBuilder.propTypes = {
  classes: PropTypes.object,
  segment: PropTypes.object,
  onClone: PropTypes.func,
  onCancel: PropTypes.func,
  onChange: PropTypes.func,
  name: PropTypes.string,
  searchAttributes: PropTypes.array,
  subtype: PropTypes.string,
  search: PropTypes.object,
  collections: PropTypes.array,
  collectionId: PropTypes.string,
  disabled: PropTypes.bool,
  title: PropTypes.node,
  showPreview: PropTypes.bool,
  actions: PropTypes.node,
};

export default withStyles(styles)(SegmentBuilder);
